import { ApiError, apiSend } from "../lib/api.js";

/**
 * E-GTCC-49-WRITE1 — the wire shapes and error mapping of the two profile
 * self-service writes, shared by gtcc1 (`ProfilePage.tsx`) and the gtcc
 * profile view (`gtcc/views/prof/GtccProfileView.tsx`).
 *
 * Both callers post the same bodies to the same routes
 * (`services/api/src/modules/identity/me.routes.ts`, POST /v1/me/password
 * and POST /v1/me/email) and read the same `error` codes back. Pulling the
 * mapping out here keeps a code the server adds later from being handled in
 * one console and not the other. The mapping returns catalog KEYS, not text:
 * gtcc1 renders them as a single message line, the gtcc view attaches them
 * to the field the server was talking about, and neither behaviour changes
 * by sharing the table.
 *
 * `null` from the error mappers means "not an API error" — the caller keeps
 * its own fallback (gtcc1 shows `err.message`, as before).
 */

export const ME_PASSWORD_PATH = "/v1/me/password";
export const ME_EMAIL_PATH = "/v1/me/email";

/** Server-side minimum the password schema enforces; mirrored for the client check. */
export const PASSWORD_MIN_LENGTH = 8;
/** Same shape check gtcc1 has always applied before sending. */
export const EMAIL_FORMAT = /^\S+@\S+\.\S+$/;

export interface PasswordChangeInput {
  readonly currentPassword: string;
  readonly newPassword: string;
}

export interface EmailChangeInput {
  readonly newEmail: string;
}

/** Answer of `POST /v1/me/email`: the stored address and its verification state. */
export interface EmailChangeResult {
  readonly email: string;
  readonly verified: boolean;
}

/** `POST /v1/me/password` — 204 on success. */
export function changeOwnPassword(input: PasswordChangeInput): Promise<void> {
  return apiSend<void>("POST", ME_PASSWORD_PATH, input);
}

/** `POST /v1/me/email` — 200 `{ email, verified }` on success. */
export function changeOwnEmail(input: EmailChangeInput): Promise<EmailChangeResult> {
  return apiSend<EmailChangeResult>("POST", ME_EMAIL_PATH, input);
}

export type PasswordClientErrorKey =
  | "me.profile.password.error.too_short"
  | "me.profile.password.error.mismatch";

/** Client-side check before the request; `null` when the input may be sent. */
export function validatePasswordInput(
  newPassword: string,
  confirmPassword: string,
): PasswordClientErrorKey | null {
  if (newPassword.length < PASSWORD_MIN_LENGTH) return "me.profile.password.error.too_short";
  if (newPassword !== confirmPassword) return "me.profile.password.error.mismatch";
  return null;
}

export type EmailClientErrorKey = "me.profile.email.error.format";

/** Client-side check before the request; `null` when the (trimmed) address may be sent. */
export function validateEmailInput(newEmail: string): EmailClientErrorKey | null {
  return EMAIL_FORMAT.test(newEmail) ? null : "me.profile.email.error.format";
}

/** The `error` code of a failed response body, when the body carries one. */
export function apiErrorCode(err: ApiError): string | undefined {
  return err.body && typeof err.body === "object"
    ? (err.body as { readonly error?: string }).error
    : undefined;
}

/** What a failed profile write says about itself: status, body code, request id. */
export interface MeApiFailure {
  readonly status: number;
  readonly code: string | undefined;
  readonly requestId: string | undefined;
}

/**
 * Reads status, `error` code and request id out of a failed request;
 * `null` when the failure is not an API answer at all (network, abort).
 * `UnauthenticatedError` extends `ApiError`, so a 401 lands here too.
 */
export function classifyMeFailure(err: unknown): MeApiFailure | null {
  if (!(err instanceof ApiError)) return null;
  return { status: err.status, code: apiErrorCode(err), requestId: err.requestId };
}

export type PasswordServerErrorKey =
  | "me.profile.password.error.invalid_current"
  | "me.profile.password.error.policy_violation"
  | "me.profile.password.error.upstream"
  | "me.profile.password.error.generic";

/**
 * Maps a failed `POST /v1/me/password` to its catalog key:
 * 401 `invalid_current_password`, 422 `policy_violation`, 502
 * `keycloak_unreachable`; every other API failure is the generic key.
 */
export function passwordErrorKey(err: unknown): PasswordServerErrorKey | null {
  const failure = classifyMeFailure(err);
  if (!failure) return null;
  switch (failure.code) {
    case "invalid_current_password":
      return "me.profile.password.error.invalid_current";
    case "policy_violation":
      return "me.profile.password.error.policy_violation";
    case "keycloak_unreachable":
      return "me.profile.password.error.upstream";
    default:
      return "me.profile.password.error.generic";
  }
}

export type EmailServerErrorKey =
  | "me.profile.email.error.same"
  | "me.profile.email.error.in_use"
  | "me.profile.email.error.upstream"
  | "me.profile.email.error.generic";

/**
 * Maps a failed `POST /v1/me/email` to its catalog key: 409 `same_email`,
 * 409 `email_in_use`, 502 `keycloak_unreachable`; every other API failure
 * is the generic key.
 */
export function emailErrorKey(err: unknown): EmailServerErrorKey | null {
  const failure = classifyMeFailure(err);
  if (!failure) return null;
  switch (failure.code) {
    case "same_email":
      return "me.profile.email.error.same";
    case "email_in_use":
      return "me.profile.email.error.in_use";
    case "keycloak_unreachable":
      return "me.profile.email.error.upstream";
    default:
      return "me.profile.email.error.generic";
  }
}
